import React, { useEffect, useMemo, useState } from "react";
import "../../styles/UserAnalytics.css";
import UserAnalyticsIcon from "../../icons/UserAnalyticsIcon";
import userAnalyticsController from "../../controllers/userAnalyticsController";
import { userService } from "../../services/userService";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getPayload = (response) => {
  const payload = response?.data;
  return payload?.data ?? payload?.result ?? payload ?? {};
};

const formatCount = (value) =>
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const toDisplayLabel = (value) => {
  if (!value) return "-";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

function UserAnalytics() {
  useTranslationSnapshot();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchAnalytics = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await userAnalyticsController.getUserAnalytics({
          jwt: getStoredToken(),
        });

        if (
          response instanceof Error ||
          response?.isAxiosError ||
          response?.response
        ) {
          throw response;
        }

        if (isCurrentRequest) setAnalytics(getPayload(response));
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setAnalytics(null);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load user analytics.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchAnalytics();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  const categoryEngagement = useMemo(
    () =>
      (analytics?.engagementByCategories || []).map((category) => ({
        label: toDisplayLabel(category.businessType),
        value: Number(category.totalInteractions || 0),
      })),
    [analytics],
  );
  const maxCategoryValue = Math.max(
    1,
    ...categoryEngagement.map((category) => category.value),
  );
  const genderDistribution = analytics?.genderDistribution || {};
  const genderTotal =
    Number(genderDistribution.male || 0) +
    Number(genderDistribution.female || 0) +
    Number(genderDistribution.custom || 0);
  const maleDegrees = genderTotal
    ? (Number(genderDistribution.male || 0) / genderTotal) * 360
    : 0;
  const femaleDegrees = genderTotal
    ? maleDegrees + (Number(genderDistribution.female || 0) / genderTotal) * 360
    : 0;
  const donutBackground = genderTotal
    ? `conic-gradient(#fa9c03 0deg ${maleDegrees}deg, #1f140f ${maleDegrees}deg ${femaleDegrees}deg, #667085 ${femaleDegrees}deg 360deg)`
    : undefined;

  return (
    <div className="user-analytics-wrapper">
      <div className="user-analytics-header">
        <div className="user-analytics-title">{translateLabel("txt_admin_user_analytics", "User Analytics")}</div>
        <div className="user-analytics-subtitle">
          Audience interacting with discovery surfaces.
        </div>
      </div>

      {loading && (
        <div className="user-analytics-state">Loading user analytics...</div>
      )}
      {!loading && error && (
        <div className="user-analytics-state user-analytics-error">{translateText(error)}</div>
      )}

      {!loading && !error && (
        <div className="user-analytics-summary-row">
          <div className="user-analytics-total-card">
            <div className="user-analytics-total-label">
              Total Platform Users
            </div>
            <div className="user-analytics-total-main">
              <div className="user-analytics-total-icon">
                <UserAnalyticsIcon stroke="#ffffff" />
              </div>
              <div className="user-analytics-total-number">
                {formatCount(analytics?.totalPlatformUsers)}
              </div>
            </div>
            <div className="user-analytics-active-label">
              Number of active user
            </div>
            <div className="user-analytics-active-number">
              {formatCount(analytics?.activeUsers)}
            </div>
            <div className="user-analytics-total-note">
              {formatCount(analytics?.activeLast30Days)} active in the last 30
              days.
            </div>
          </div>

          <div className="user-analytics-gender-card">
            <div className="user-analytics-card-title">Gender Distribution</div>
            <div className="user-analytics-gender-content">
              <div
                className="user-analytics-donut"
                style={
                  donutBackground ? { background: donutBackground } : undefined
                }
                aria-label={translateLabel("txt_admin_gender_distribution_chart", "Gender distribution chart")}
              />
              <div className="user-analytics-legend">
                <div className="user-analytics-legend-item">
                  <span className="user-analytics-legend-dot user-analytics-legend-dot-male" />
                  <span>{translateLabel("txt_admin_male", "Male (")}{formatCount(genderDistribution.male)})</span>
                </div>
                <div className="user-analytics-legend-item">
                  <span className="user-analytics-legend-dot user-analytics-legend-dot-female" />
                  <span>{translateLabel("txt_admin_female", "Female (")}{formatCount(genderDistribution.female)})</span>
                </div>
                <div className="user-analytics-legend-item">
                  <span className="user-analytics-legend-dot user-analytics-legend-dot-custom" />
                  <span>{translateLabel("txt_admin_custom", "Custom (")}{formatCount(genderDistribution.custom)})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* {!loading && !error && (
        <div className="user-analytics-engagement-card">
          <div className="user-analytics-card-title">
            Engagement by Categories
          </div>
          <div className="user-analytics-bar-chart">
            {categoryEngagement.length === 0 && (
              <div className="user-analytics-state">No engagement data.</div>
            )}
            {categoryEngagement.map((category) => (
              <div className="user-analytics-bar-row" key={category.label}>
                <div className="user-analytics-bar-label">{category.label}</div>
                <div className="user-analytics-bar-track">
                  <div
                    className="user-analytics-bar-fill"
                    style={{
                      width: `${(category.value / maxCategoryValue) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="user-analytics-axis">
            <span>0</span>
            <span>{formatCount(Math.round(maxCategoryValue * 0.25))}</span>
            <span>{formatCount(Math.round(maxCategoryValue * 0.75))}</span>
            <span>{formatCount(maxCategoryValue)}</span>
          </div>
        </div> */}
      {/* )} */}
    </div>
  );
}

export default UserAnalytics;
